import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { symbolKey } from '@/entities/symbol';
import { captureEvent } from '@/services/analytics';
// eslint-disable-next-line sonarjs/todo-tag -- intentional migration marker
// TODO: move to store/slices/ and update once drawerSlice is migrated to a feature
import { closeDrawer } from '@/store/slices/drawer.slice';

import type { ConflictingMode } from '../constants';
import { MODE_CONFLICT_MESSAGES } from '../constants';
import {
  addForwardCurves,
  addSymbol,
  addSymbols,
  requestAddForwardCurves,
  requestForwardCurveSelection,
  requestSpotSelection,
  restoreSymbols,
  SELECTOR_MODES,
  setSelectorMode,
  setSymbols,
  toggleForwardCurve,
  toggleSymbol
} from './symbolSelector.slice';
import type { SymbolSelectorState } from './symbolSelector.slice';

type WithSymbolSelector = { symbolSelector: SymbolSelectorState };
type IdleCallbackGlobal = typeof globalThis & {
  requestIdleCallback?: (callback: () => void) => void;
};

function hasModeConflict(
  current: SELECTOR_MODES,
  allowed: SELECTOR_MODES
): current is ConflictingMode {
  return current !== SELECTOR_MODES.NEUTRAL && current !== allowed;
}

function showModeConflictSnackbar(conflictingMode: ConflictingMode): void {
  enqueueSnackbar(MODE_CONFLICT_MESSAGES[conflictingMode], {
    variant: 'warning',
    autoHideDuration: 10000
  });
}

export const selectionListener = createListenerMiddleware();

selectionListener.startListening({
  matcher: isAnyOf(addSymbol, addSymbols, setSymbols, toggleSymbol, toggleForwardCurve),
  effect: (action, api) => {
    const prev = (api.getOriginalState() as WithSymbolSelector).symbolSelector;
    const next = (api.getState() as WithSymbolSelector).symbolSelector;
    const isForwardMode = next.selectorMode === SELECTOR_MODES.FORWARD_CURVE;

    const prevKeys = new Set<string>(prev.symbols.map(s => symbolKey(s, isForwardMode)));
    const added = next.symbols.filter(s => !prevKeys.has(symbolKey(s, isForwardMode)));
    if (added.length) {
      // Defer analytics to avoid blocking the UI thread during interactions
      const schedule = (fn: () => void) => {
        const idleGlobal = globalThis as IdleCallbackGlobal;
        if (idleGlobal.requestIdleCallback) {
          idleGlobal.requestIdleCallback(fn);
        } else {
          setTimeout(fn, 0);
        }
      };
      schedule(() => {
        for (const symbol of added) {
          try {
            captureEvent('symbol_select', { symbol });
          } catch {
            // non-fatal
          }
        }
      });
    }
  }
});

selectionListener.startListening({
  actionCreator: closeDrawer,
  effect: (_action, api) => {
    const { previousSymbols } = (api.getState() as WithSymbolSelector).symbolSelector;
    if (previousSymbols !== null) {
      api.dispatch(restoreSymbols());
    }
  }
});

selectionListener.startListening({
  actionCreator: requestSpotSelection,
  effect: ({ payload }, api) => {
    const { selectorMode } = (api.getState() as WithSymbolSelector).symbolSelector;
    if (hasModeConflict(selectorMode, SELECTOR_MODES.SPOT)) {
      showModeConflictSnackbar(selectorMode);
      return;
    }
    if ('symbols' in payload) {
      if (!payload.symbols.length) return;
      // Set mode first so addSymbols uses spot-mode key matching.
      api.dispatch(setSelectorMode(SELECTOR_MODES.SPOT));
      api.dispatch(addSymbols({ symbols: payload.symbols }));
    } else {
      // Set mode first so toggleSymbol uses spot-mode key matching.
      api.dispatch(setSelectorMode(SELECTOR_MODES.SPOT));
      api.dispatch(toggleSymbol({ symbol: payload.symbol }));
    }
  }
});

selectionListener.startListening({
  actionCreator: requestForwardCurveSelection,
  effect: ({ payload }, api) => {
    const { selectorMode } = (api.getState() as WithSymbolSelector).symbolSelector;
    if (hasModeConflict(selectorMode, SELECTOR_MODES.FORWARD_CURVE)) {
      showModeConflictSnackbar(selectorMode);
      return;
    }
    const { symbols } = payload;
    if (!symbols.length) return;

    api.dispatch(setSelectorMode(SELECTOR_MODES.FORWARD_CURVE));
    api.dispatch(toggleForwardCurve({ symbols }));
  }
});

selectionListener.startListening({
  actionCreator: requestAddForwardCurves,
  effect: ({ payload }, api) => {
    const { selectorMode } = (api.getState() as WithSymbolSelector).symbolSelector;
    if (hasModeConflict(selectorMode, SELECTOR_MODES.FORWARD_CURVE)) {
      showModeConflictSnackbar(selectorMode);
      return;
    }
    const { symbols } = payload;
    if (!symbols.length) return;

    api.dispatch(setSelectorMode(SELECTOR_MODES.FORWARD_CURVE));
    api.dispatch(addForwardCurves({ symbols }));
  }
});
